import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline'
import '@tailwindplus/elements'
import React, { useCallback, useEffect, useState } from 'react'
import { DragDropProvider as BaseDragDropProvider, DragEndEvent } from '@dnd-kit/react'
import { CollisionDetector, closestCorners } from '@dnd-kit/collision'
import { isSortable } from '@dnd-kit/react/sortable'
import { DirectUpload, t } from 'shared'
import { ImageItem, ImageUploadProps } from './types'
import AddButton from './add_button'
import SortableImage from './sortable_image'

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'el-popover': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        popover?: string
        anchor?: string
      }
    }
  }
}

const DragDropProvider = BaseDragDropProvider as React.FC<{
  onDragEnd?: (event: DragEndEvent) => void
  collisionDetector?: CollisionDetector
  children?: React.ReactNode
}>

export const ImageUpload = ({ images: imagesProp, onChange, getCropUrl, error }: ImageUploadProps) => {
  const [images, setImages] = useState<ImageItem[]>(imagesProp)
  const [fileSizeError, setFileSizeError] = useState<string | null>(null)

  useEffect(() => {
    setImages(imagesProp)
  }, [imagesProp])

  const handleUpload = useCallback(
    (signedId: string, url?: string) => {
      setImages((prev) => {
        const next = [...prev, { signedId, url: url ?? '' }]
        onChange(next)
        return next
      })
    },
    [onChange]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.canceled) return

      const { source } = event.operation

      // 1. Guard check: ensure the source exists and is a sortable item
      if (!source || !isSortable(source)) return

      // 2. Extract the starting and ending indices directly from the sortable state
      const { initialIndex, index } = source.sortable

      // 3. If the item was dropped in its original position, do nothing
      if (initialIndex === index) return

      setImages((prev) => {
        const next = [...prev]

        // 4. Move the item using the indices provided by dnd-kit
        const [moved] = next.splice(initialIndex, 1)
        next.splice(index, 0, moved)

        onChange(next)

        return next
      })
    },
    [onChange]
  )

  const handleDelete = useCallback(
    (index: number) => {
      setImages((prev) => {
        const next = prev.filter((_, i) => i !== index)
        onChange(next)
        return next
      })
    },
    [onChange]
  )

  return (
    <div>
      <DragDropProvider onDragEnd={handleDragEnd} collisionDetector={closestCorners}>
        <div className="flex flex-wrap gap-2 border-2 border-dashed border-gray-300 rounded-xl p-3">
          {images.length < 6 && (
            <DirectUpload
              onUpload={handleUpload}
              triggerComponent={AddButton}
              error={error ?? undefined}
              onError={setFileSizeError}
            />
          )}
          {images.map((image, index) => (
            <SortableImage
              image={image}
              index={index}
              key={image.signedId}
              imagesCount={images.length}
              handleDelete={handleDelete}
              getCropUrl={getCropUrl}
            />
          ))}
        </div>
      </DragDropProvider>
      {images.length > 0 && (
        <p className="mt-2 text-sm text-gray-500 flex items-center gap-1">
          <ArrowsRightLeftIcon className="inline w-4 h-4" />
          {t('image_upload.drag_to_reorder')}
        </p>
      )}
      {fileSizeError && <p className="mt-1 text-sm text-red-600">{fileSizeError}</p>}
      <p className="mt-1 text-sm text-gray-500">{t('image_upload.max_photos')}</p>
    </div>
  )
}
